import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    FileText,
    ShieldCheck,
    TrendingUp,
    RefreshCcw,
    Clock,
    ClipboardCheck,
    MessageSquare,
    Handshake,
    Calculator,
    ClipboardList
} from 'lucide-react';
import toast from 'react-hot-toast';

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/stats');
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            toast.error('Failed to load dashboard statistics');
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        {
            title: 'Total Pages',
            value: stats?.totalPages || '0',
            subtitle: 'All published',
            icon: <FileText className="text-white" size={24} />,
            iconBg: 'bg-indigo-600',
            status: 'Active',
            statusColor: 'text-indigo-600 bg-indigo-50',
            trend: null
        },
        {
            title: 'Total Admins',
            value: stats?.totalAdmins || '0',
            subtitle: 'All verified',
            icon: <ShieldCheck className="text-white" size={24} />,
            iconBg: 'bg-purple-500',
            status: 'Active',
            statusColor: 'text-purple-600 bg-purple-50',
            trend: null
        },
        {
            title: 'Contact Enquiries',
            value: stats?.totalContactEnquiries?.toLocaleString() || '0',
            subtitle: 'Total submissions',
            icon: <MessageSquare className="text-white" size={24} />,
            iconBg: 'bg-blue-500',
            status: 'Live',
            statusColor: 'text-blue-600 bg-blue-50',
            trend: null
        },
        {
            title: 'DSA Partner Enquiries',
            value: stats?.totalDSAEnquiries?.toLocaleString() || '0',
            subtitle: 'Partnership requests',
            icon: <Handshake className="text-white" size={24} />,
            iconBg: 'bg-emerald-500',
            status: 'Active',
            statusColor: 'text-emerald-600 bg-emerald-50',
            trend: null
        },
        {
            title: 'Credit Score Enquiries',
            value: stats?.totalCreditScoreEnquiries?.toLocaleString() || '0',
            subtitle: 'Score check requests',
            icon: <Calculator className="text-white" size={24} />,
            iconBg: 'bg-pink-500',
            status: 'Active',
            statusColor: 'text-pink-600 bg-pink-50',
            trend: null
        },
        {
            title: 'Eligibility Enquiries',
            value: stats?.totalEligibilityEnquiries?.toLocaleString() || '0',
            subtitle: 'Eligibility checks',
            icon: <ClipboardList className="text-white" size={24} />,
            iconBg: 'bg-cyan-500',
            status: 'Live',
            statusColor: 'text-cyan-600 bg-cyan-50',
            trend: null
        },
        {
            title: 'Eligibility Scorecard',
            value: stats?.totalScorecardQuestions || '14',
            subtitle: 'Active questions',
            icon: <ClipboardCheck className="text-white" size={24} />,
            iconBg: 'bg-orange-500',
            status: 'Live',
            statusColor: 'text-orange-600 bg-orange-50',
            trend: null,
            link: '/admin/eligibility-scorecard'
        }
    ];

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Dashboard Overview</h1>
                    <p className="text-slate-500 font-medium mt-1">Welcome back! Here's what's happening with your website.</p>
                </div>
                 {/* <button
                    onClick={fetchStats}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-600 font-bold hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm active:scale-95 disabled:opacity-50"
                >
                    <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
                    Refresh Stats
                </button> */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((card, idx) => {
                    const CardWrapper = card.link ? 'a' : 'div';
                    const cardProps = card.link ? { href: card.link } : {};

                    return (
                        <CardWrapper
                            key={idx}
                            {...cardProps}
                            className={`group bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 transform hover:-translate-y-1 ${card.link ? 'cursor-pointer' : ''}`}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div className={`${card.iconBg} p-3.5 rounded-2xl shadow-lg ring-4 ring-white`}>
                                    {card.icon}
                                </div>
                                <span className={`text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider ${card.statusColor}`}>
                                    {card.status}
                                </span>
                            </div>

                            <div className="space-y-1">
                                <p className="text-slate-500 font-medium text-sm">{card.title}</p>
                                <div className="flex items-baseline gap-2">
                                    <h3 className="text-3xl font-black text-slate-800 tracking-tight">{card.value}</h3>
                                    {card.trend && (
                                        <TrendingUp className="text-emerald-500" size={16} />
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-dashed border-slate-100 flex items-center gap-2">
                                <Clock size={14} className="text-slate-400" />
                                <p className="text-xs font-semibold text-slate-400">{card.subtitle}</p>
                            </div>
                        </CardWrapper>
                    );
                })}
            </div>

            {/* Placeholder for charts/activity - can be added later as needed */}
            {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 p-8 shadow-sm min-h-[400px] flex flex-col items-center justify-center text-center space-y-4">
                    <div className="bg-slate-50 p-6 rounded-full">
                        <TrendingUp className="text-slate-200" size={48} />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-slate-800">Traffic Analysis</h3>
                        <p className="text-slate-400 max-w-xs mx-auto mt-1 font-medium">Detailed traffic and conversion charts will appear here as more data is collected.</p>
                    </div>
                </div>

                <div className="bg-[#283079] rounded-3xl p-8 shadow-xl relative overflow-hidden flex flex-col justify-between">
                    <div className="absolute top-0 right-0 p-8 opacity-10">
                        <ShieldCheck size={120} className="text-white" />
                    </div>

                    <div className="relative z-10">
                        <h3 className="text-2xl font-bold text-white mb-2">System Security</h3>
                        <p className="text-white/60 font-medium">Your administration panel is protected with banking-grade encryption and monitoring.</p>
                    </div>

                    <div className="relative z-10 space-y-4 mt-8">
                        <div className="flex items-center gap-3 bg-white/10 p-4 rounded-2xl border border-white/10 backdrop-blur-sm">
                            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                            <p className="text-sm font-bold text-white">Database Cluster: Healthy</p>
                        </div>
                        <div className="flex items-center gap-3 bg-white/10 p-4 rounded-2xl border border-white/10 backdrop-blur-sm">
                            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                            <p className="text-sm font-bold text-white">SSL Encryption: Verified</p>
                        </div>
                        <div className="flex items-center gap-3 bg-white/10 p-4 rounded-2xl border border-white/10 backdrop-blur-sm">
                            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                            <p className="text-sm font-bold text-white">IP Monitoring: Active</p>
                        </div>
                    </div>
                </div>
            </div> */}
        </div>
    );
};

export default Dashboard;
